import { useState, useEffect } from "react";
import { supabase } from "../auth/supabaseClient";
import { AiFillCheckCircle, AiOutlineTeam } from "react-icons/ai";
import { useAuth } from "../../contexts/AuthContext";
import { isDisallowedEmail } from "../../utils/general";
import { StepBar } from "../common/steps";
import { useNavigate } from "react-router-dom";

const TeamJoinSearch = (props) => {
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [teamRequestSent, setTeamRequestSent] = useState(false);

  const createPendingTeamMember = async (team) => {
    const { data, error } = await supabase
      .from("team_membership")
      .insert([
        { team_id: team.id, user_id: user.id, role: "unapproved-member" },
      ]);

    setTeamRequestSent(true);

    props.setTeamName(team.team_name);
    props.setTeamSectionComplete(true);
    props.setTeamsModified(true);
  };

  const getSearchResults = async () => {
    if (search === "") {
      setSearchResults([]);
      return;
    }
    const { data, error } = await supabase
      .from("teams")
      .select("id, team_name, team_domain")
      .ilike("team_name", `%${search}%`)
      .limit(5);
    if (error) {
      console.log(error);
      return;
    }
    setSearchResults(data);

    console.log(data);
  };

  useEffect(() => {
    getSearchResults();
  }, [search]);

  const generateResultRows = () => {
    let rows = searchResults.map((result) => {
      return (
        <li
          key={result.team_name}
          onClick={() => createPendingTeamMember(result)}
          className="pl-8 pr-2 py-1 border-b-2 border-gray-100 relative cursor-pointer hover:bg-yellow-50 hover:text-gray-900"
        >
          <svg
            className="absolute w-4 h-4 left-2 top-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          <b>{result.team_name}</b>
          <a className="absolute right-2 bottom-2 text-xs">join team</a>
        </li>
      );
    });

    let teamNames = searchResults.map((result) => result.team_name);

    if (search === "") {
      return null;
    } else if (
      !teamNames.some((x) => x.toLowerCase() == search.toLowerCase())
    ) {
      console.log("just before", rows);
      rows.push(
        <li
          key="unique-team-creation"
          onClick={() => props.handleCreateTeam(search)}
          className="pl-8 pr-2 py-1 border-b-2 border-gray-100 relative cursor-pointer hover:bg-yellow-50 hover:text-gray-900"
        >
          <AiFillCheckCircle
            color="green"
            className="absolute w-4 h-4 left-2 top-2"
          />
          <b>
            <a className="text-purpler">{search}</a>
          </b>
          <a className="absolute right-2 bottom-2 text-xs">create team</a>
        </li>
      );
    } else if (rows.length === 0) {
      return (
        <li
          key="unique-no-results"
          className="pl-8 pr-2 py-1 border-b-2 border-gray-100 relative cursor-pointer hover:bg-yellow-50 hover:text-gray-900"
        >
          <svg
            className="absolute w-4 h-4 left-2 top-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          <b>No results found</b>
        </li>
      );
    }

    return rows;
  };

  return (
    <div className="">
      <div className="mb-4 inline-flex flex-col justify-center relative text-gray-500">
        <label className="block text-grey-darker text-sm mb-1" htmlFor="XXXXXX">
          Find or create a team
        </label>
        <div hidden={teamRequestSent} className="relative">
          <input
            type="text"
            className="p-2 pl-8 w-96 rounded border border-gray-200 bg-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:border-transparent"
            placeholder="search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg
            className="w-4 h-4 absolute left-2.5 top-3.5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <ul
          hidden={teamRequestSent}
          className="bg-white border border-gray-100 w-full mt-2"
        >
          {generateResultRows()}
        </ul>
        <p hidden={!teamRequestSent} className="text-green-500 text-xs italic">
          You've sent a request to {props.teamName} to be added to the team.
        </p>
      </div>
    </div>
  );
};

const TeamConfigWidget = (props) => {
  const { user } = useAuth();

  const [createTeam, setCreateTeam] = useState(false);
  const [joinTeam, setJoinTeam] = useState(false);

  const [teamName, setTeamName] = useState("");
  const [teamDomain, setTeamDomain] = useState("");

  const [disableDomainClaim, setDisableDomainClaim] = useState(false);
  const [isDomainClaimed, setIsDomainClaimed] = useState(false);
  const [isDomainClaimedInSession, setIsDomainClaimedInSession] =
    useState(false);

  useEffect(() => {
    if (user) {
      setTeamDomain(user.email.split("@")[1]);
      setDisableDomainClaim(isDisallowedEmail(user.email));
    }
  }, [teamName, user]);

  useEffect(() => {
    if (teamDomain) {
      fetchIsDomainClaimed();
    }
  }, [teamDomain]);

  const handleCreateTeam = async (teamName) => {
    setCreateTeam(true);
    setTeamName(teamName);

    try {
      // Create team with team name
      const { data, teamCreationError } = await supabase
        .from("teams")
        .insert([{ team_name: teamName }])
        .select("*");

      if (teamCreationError) throw teamCreationError;
      console.log("Team created!", data);

      // Add user to team
      const { userAdditionError } = await supabase
        .from("team_membership")
        .insert([
          {
            team_id: data[0].id,
            user_id: user.id,
            role: "admin",
          },
        ]);
    } catch (teamCreationError) {
      alert(teamCreationError.message);
    }

    props.setTeamSectionComplete(true);
    props.setTeamsModified(true);
  };

  const fetchIsDomainClaimed = async () => {
    const { data, error } = await supabase
      .from("teams")
      .select("*")
      .eq("team_domain", teamDomain);

    if (error) throw error;
    console.log("Team domain claimed?", data);
    setIsDomainClaimed(data.length > 0);
  };

  const handleClaimDomain = async (e) => {
    e.preventDefault();

    try {
      const { data, error } = await supabase
        .from("teams")
        .update({ team_domain: teamDomain })
        .eq("team_name", props.teamName)
        .select();

      if (error) throw error;

      console.log("Domain claimed!", data);
      setIsDomainClaimed(true);
      setIsDomainClaimedInSession(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const renderTeamWidget = () => {
    if (!createTeam && !joinTeam) {
      return (
        <div className="mb-4">
          <TeamJoinSearch
            setTeamName={setTeamName}
            setTeamsModified={props.setTeamsModified}
            setTeamSectionComplete={props.setTeamSectionComplete}
            handleCreateTeam={handleCreateTeam}
          />
        </div>
      );
    } else if (createTeam) {
      return (
        <div className="mb-4">
          <div className="mb-4">
            <div className="flex justify-center">
              You've created team
              <p className="text-purpler ml-1"> {props.teamName}</p>
            </div>
          </div>

          {!isDomainClaimed && !disableDomainClaim && (
            <>
              <label
                className="block text-grey-darker text-sm mb-1"
                htmlFor="XXXXXX"
              >
                Claim Team Domain
              </label>

              <button
                onClick={handleClaimDomain}
                type="button"
                className="text-xs inline-flex justify-center items-center bg-slate-500 text-white font-bold w-full py-2 px-4 rounded"
              >
                Claim {teamDomain} on Ribbon
              </button>
            </>
          )}

          {isDomainClaimed && !isDomainClaimedInSession && (
            <p className="text-slate-500 text-xs italic">
              You can't claim <a className="text-purpler">{teamDomain}</a> It's
              already claimed.
            </p>
          )}

          {isDomainClaimed && isDomainClaimedInSession && (
            <p className="text-slate-500 text-xs italic">
              You have successfully claimed{" "}
              <a className="text-purpler">{teamDomain}</a>
            </p>
          )}
        </div>
      );
    } else if (joinTeam) {
      return null;
    }
  };

  return renderTeamWidget();
};

export default function SetTeam() {
  const { user } = useAuth();

  const [teamSectionComplete, setTeamSectionComplete] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsModified, setTeamsModified] = useState(false);

  const navigate = useNavigate();

  const handleNext = () => {
    navigate("/set-plan");
  };

  const fetchExistingTeamMembership = async () => {
    const { data: teamMembershipData, error: teamMembershipError } =
      await supabase.from("team_membership").select("*").eq("user_id", user.id);

    if (teamMembershipError) throw teamMembershipError;
    console.log("Team member?", teamMembershipData);

    if (teamMembershipData.length > 0) {
      setTeamSectionComplete(true);
      const { data: teamData, error: teamError } = await supabase
        .from("teams")
        .select("*")
        .in(
          "id",
          teamMembershipData.map((team) => team.team_id)
        );

      if (teamError) throw teamError;

      // get role from team membership
      teamData.map((team) => {
        const teamMembership = teamMembershipData.find(
          (teamMembership) => teamMembership.team_id === team.id
        );
        team.role = teamMembership.role;
      });

      setTeams(teamData);
    }
  };

  useEffect(() => {
    if (user) {
      fetchExistingTeamMembership();
    }
  }, [user, teamsModified]);

  return (
    <div className="w-xl">
      <StepBar
        steps={[
          { label: "Profile", status: "complete" },
          { label: "Wallet", status: "complete" },
          { label: "Team", status: "current" },
          { label: "Plan", status: "incomplete" },
        ]}
      />
      <div className="rounded max-w-xl p-5 bg-slate-100">
        <h1 className="text-2xl font-bold mb-5 flex items-center">
          <AiOutlineTeam size={40} />
          Your Teams
        </h1>
        <TeamDisplayWidget teams={teams} />
        <TeamConfigWidget
          setTeamsModified={setTeamsModified}
          setTeamSectionComplete={setTeamSectionComplete}
        />
        <button
          onClick={handleNext}
          hidden={!teamSectionComplete}
          className="bg-purpler text-white font-bold py-2 px-4 rounded"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function TeamDisplayWidget(props) {
  const generateTeam = (team) => {
    return (
      <div className="flex items-center mb-2" key={team.id}>
        <p className="text-purpler ml-1"> {team.team_name}</p>
        {team.role == "unapproved-member" && (
          <p className="text-xs text-gray-500 ml-1">(pending)</p>
        )}
      </div>
    );
  };

  return (
    <div className="mb-4">
      <div className="mb-4">
        <div className="grid-cols-1">
          {props.teams.map((team) => generateTeam(team))}
        </div>
      </div>
    </div>
  );
}
